/**
 * Maps the unified medication-history response (Component D) into the
 * presentational view-models the MedicationsCard renders.
 *
 * Encodes the write-contract rules from the 2026-07-15 backend spike
 * (artifacts/planning/gdn-2351-e-plan.md §3.6):
 *  - `inactive_reason='removed'` is the soft-DELETE state → filtered out of the
 *    card entirely (distinct from `pharmacy_history` inactives, which render).
 *  - Status vocabulary maps 1:1 onto the episode chips, keeping held ≠ stopped.
 */

import type {
  EpisodeStatus,
  HistoryFill,
  MedicationEpisodeVM,
  MedicationOrderVM,
  MedicationSource,
  MedicationWorkspaceVM,
  Provenance,
  RegimenInterval,
  TimelineEventKind,
  TimelineEventVM,
} from './types';
import type {
  SuggestedMedicationApi,
  UnifiedConsentState,
  UnifiedMedication,
  UnifiedMedicationEvent,
  UnifiedMedicationHistoryResponse,
} from './unifiedApiTypes';

const STATUS_TO_EPISODE: Record<UnifiedMedication['status'], EpisodeStatus> = {
  active: 'current',
  held: 'held',
  discontinued: 'stopped',
  inactive: 'historical',
};

const SOURCE_TO_VM: Record<string, MedicationSource> = {
  dosespot_rx: 'dosespot_order',
  dosespot_self_reported: 'patient_report',
  surescripts: 'surescripts_history',
  jotpsych: 'clinician_manual',
  data_migration: 'transition_import',
};

const KIND_TO_TIMELINE: Record<string, TimelineEventKind> = {
  prescription_written: 'sent',
  prescription_status_changed: 'sent',
  fill_recorded: 'fill_recorded',
  self_reported_added: 'started',
  self_reported_edited: 'use_changed',
  self_reported_removed: 'stopped',
  discontinued: 'stopped',
  completed: 'completed',
  held: 'held',
  resumed: 'restarted',
  prior_auth_status: 'use_changed',
};

export const mapSource = (source: string): MedicationSource =>
  SOURCE_TO_VM[source] ?? 'clinician_manual';

/** Human label per event — increase/decrease/hold language, never "new". */
export const eventLabel = (event: UnifiedMedicationEvent): string => {
  switch (event.event_kind) {
    case 'prescription_written':
      return event.strength ? `Prescription written — ${event.strength}` : 'Prescription written';
    case 'prescription_status_changed':
      return event.dosespot_medication_status
        ? `Prescription status: ${event.dosespot_medication_status}`
        : 'Prescription status changed';
    case 'fill_recorded':
      return event.pharmacy_name ? `Filled at ${event.pharmacy_name}` : 'Filled';
    case 'self_reported_added':
      return 'Added to chart';
    case 'self_reported_edited':
      return event.strength ? `Updated — ${event.strength}` : 'Updated';
    case 'self_reported_removed':
      return 'Removed from chart';
    case 'discontinued':
      return event.discontinue_reason
        ? `Discontinued — ${event.discontinue_reason}`
        : 'Discontinued';
    case 'completed':
      return 'Course completed';
    case 'held':
      return 'Held';
    case 'resumed':
      return 'Resumed';
    case 'prior_auth_status':
      return 'Prior authorization update';
    default:
      return event.event_kind.replaceAll('_', ' ');
  }
};

const toTimelineEvent = (event: UnifiedMedicationEvent): TimelineEventVM => ({
  id: event.medication_event_id,
  date: event.event_at,
  kind: KIND_TO_TIMELINE[event.event_kind] ?? 'use_changed',
  label: eventLabel(event),
  detail: event.directions ?? undefined,
  source: mapSource(event.source),
  prescriber: event.prescriber_name ?? undefined,
});

/**
 * Order-ish detail for the AMD columns. Each field is derived independently —
 * the newest event where THAT field is non-null — because events carry these
 * fields sparsely (a fill may name refills but no pharmacy while an older
 * prescription_written has both); binding all fields to one event silently
 * nulls data that exists a few events back (review finding on #2339).
 */
const deriveOrder = (medication: UnifiedMedication): MedicationOrderVM | null => {
  const events = medication.events ?? []; // newest first
  const newestWith = (field: keyof UnifiedMedicationEvent) =>
    events.find((event) => event[field] != null);

  const prescriberEvent = newestWith('prescriber_name');
  const pharmacyEvent = newestWith('pharmacy_name');
  const refillsEvent = newestWith('refills');
  const anchor = prescriberEvent ?? pharmacyEvent ?? refillsEvent;
  if (!anchor) return null;

  const newestWritten = events.find((event) => event.event_kind === 'prescription_written');
  const newestFill = events.find((event) => event.event_kind === 'fill_recorded');

  return {
    id: anchor.medication_event_id,
    orderStatus: 'sent',
    rawVendorStatus: newestWith('dosespot_medication_status')?.dosespot_medication_status ?? undefined,
    writtenDate: newestWritten?.event_at ?? null,
    lastFillDate: newestFill?.event_at ?? null,
    prescriber: prescriberEvent?.prescriber_name ?? null,
    pharmacy: pharmacyEvent?.pharmacy_name ?? null,
    refills: refillsEvent?.refills ?? null,
    daysSupply: newestWith('days_supply')?.days_supply ?? null,
  };
};

const deriveRegimen = (medication: UnifiedMedication): RegimenInterval | undefined => {
  const doseText = medication.latest_strength;
  const frequency = medication.latest_directions;
  // Clinical dates (charted start/end) beat event timestamps: for
  // migrated/legacy meds first_event_at is the ingest time, so without the
  // preference a med started in 2019 reads "started Jul 2026".
  const hasClinicalDates = Boolean(medication.start_date || medication.end_date);
  if (!doseText && !frequency && !hasClinicalDates) return undefined;
  const stopped = medication.status === 'discontinued' || medication.status === 'inactive';
  return {
    type: 'prescribed',
    doseText: doseText ?? '',
    frequency: frequency ?? '',
    start: {
      value: medication.start_date ?? medication.first_event_at,
      precision: 'exact',
    },
    end: stopped
      ? {
          value:
            medication.end_date ??
            medication.discontinued_at ??
            medication.last_event_at,
          precision: 'exact',
        }
      : undefined,
  };
};

const toEpisode = (medication: UnifiedMedication): MedicationEpisodeVM => {
  const order = deriveOrder(medication);
  const events = medication.events ?? [];
  // asOf is per-source: that source's own newest event (events arrive newest
  // first), not the medication's overall newest — a stale pharmacy claim must
  // not borrow a fresh renewal's date.
  const sources: Provenance[] = (medication.sources ?? []).map((source) => ({
    source: mapSource(source),
    asOf:
      events.find((event) => event.source === source)?.event_at ??
      medication.last_event_at,
  }));

  return {
    id: medication.medication_id,
    drugName: medication.display_name,
    fullName: medication.generic_name ?? medication.display_name,
    rxcui: medication.rxcui ?? undefined,
    episodeStatus: STATUS_TO_EPISODE[medication.status],
    verificationStatus: 'unreviewed',
    prescribed: deriveRegimen(medication),
    prescribedDiffers: false,
    target: medication.indication ?? undefined,
    sources,
    orders: order ? [order] : [],
    timeline: events.map(toTimelineEvent),
  };
};

/**
 * Pharmacy-only medications (never prescribed here) render as fills in the
 * consent-gated history section, not as table rows. Fill rows come from the
 * medication's fill_recorded events; a fill-less pharmacy med (edge case)
 * falls back to its newest event so it isn't silently dropped.
 */
const toHistoryFills = (medication: UnifiedMedication): HistoryFill[] => {
  const events = medication.events ?? [];
  const fillEvents = events.filter((event) => event.event_kind === 'fill_recorded');
  const source = fillEvents.length > 0 ? fillEvents : events.slice(0, 1);
  return source.map((event) => ({
    id: event.medication_event_id,
    drugName: medication.display_name,
    sig: event.directions ?? undefined,
    quantity: event.quantity != null ? String(event.quantity) : undefined,
    daysSupply: event.days_supply ?? undefined,
    lastFillDate: event.event_at,
    payer: event.payer_name ?? '—',
  }));
};

export interface UnifiedCardData {
  workspace: MedicationWorkspaceVM;
  consentState: UnifiedConsentState;
  /** Pharmacy-claim fills for the consent-gated history section, newest first. */
  historyFills: HistoryFill[];
  /**
   * SureScripts fills that matched an already-charted medication and so live
   * in that medication's drawer, not the history section. Lets the section's
   * empty state say "fills exist, attached above" instead of the misleading
   * "no data from SureScripts yet".
   */
  attributedFillCount: number;
}

/**
 * Response → card props. Routing rules (e-plan §3.4/§3.6):
 *  - `inactive_reason='removed'` (the soft-DELETE state) → dropped entirely.
 *  - `inactive_reason='pharmacy_history'` (pharmacy-only, never prescribed
 *    here) → the history-fills section, not the table.
 *  - everything else → an episode row.
 */
/**
 * A pending AI chart-proposal as a reviewable episode. The id is the
 * proposal's action_id: the existing PATCH/DELETE medication routes accept
 * it (accept-with-edits / dismiss), so the container's edit and remove
 * callbacks work unchanged. Rendered FIRST — these need clinician review.
 */
const toSuggestedEpisode = (suggested: SuggestedMedicationApi): MedicationEpisodeVM => {
  const doseText = suggested.strength ?? suggested.dosage;
  const frequency =
    suggested.frequency && suggested.timing
      ? `${suggested.frequency} (${suggested.timing})`
      : (suggested.frequency ?? suggested.timing);
  return {
    id: suggested.action_id,
    drugName: suggested.name,
    fullName: suggested.generic_name ?? suggested.name,
    episodeStatus: 'suggested',
    isSuggested: true,
    verificationStatus: 'unreviewed',
    prescribed:
      doseText || frequency || suggested.start_date
        ? {
            type: 'prescribed',
            doseText: doseText ?? '',
            frequency: frequency ?? '',
            // start is required on the interval; an empty value renders
            // as the em dash in the Date cell.
            start: { value: suggested.start_date ?? '', precision: 'exact' },
            end: suggested.end_date
              ? { value: suggested.end_date, precision: 'exact' }
              : undefined,
          }
        : undefined,
    target: suggested.indication ?? undefined,
    sources: [],
    orders: [],
    timeline: [],
  };
};

export const mapUnifiedToVM = (
  response: UnifiedMedicationHistoryResponse,
): UnifiedCardData => {
  const kept = (response.medications ?? []).filter(
    (medication) => medication.inactive_reason !== 'removed',
  );
  const pharmacyOnly = kept.filter(
    (medication) => medication.inactive_reason === 'pharmacy_history',
  );
  const charted = kept.filter(
    (medication) => medication.inactive_reason !== 'pharmacy_history',
  );

  return {
    workspace: {
      patient: { name: '', dob: '' },
      lastVerified: null,
      changesSinceLastEncounter: 0,
      episodes: [
        ...(response.suggested_medications ?? []).map(toSuggestedEpisode),
        ...charted.map(toEpisode),
      ],
      reconciliationCandidates: [],
      attention: [],
      syncStatus: { lastSync: '', failures: 0 },
    },
    consentState: response.surescripts_consent?.state ?? 'needed',
    historyFills: pharmacyOnly
      .flatMap(toHistoryFills)
      .sort((a, b) => b.lastFillDate.localeCompare(a.lastFillDate)),
    attributedFillCount: charted.reduce(
      (count, medication) =>
        count +
        (medication.events ?? []).filter(
          (event) =>
            event.source === 'surescripts' && event.event_kind === 'fill_recorded',
        ).length,
      0,
    ),
  };
};
